import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { Toaster } from 'sonner';
import { updateCoverImages, updateChoices } from './utils/layout';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from '@/auth/auth-context';
import { RequireAuth } from '@/routing/require-auth';
import { asset } from './utils/asset';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import CustomAvisoNovaVersao from '@/components/CustomAvisoNovaVersao';
import CustomFaixaVerComo from '@/components/CustomFaixaVerComo';

// Make asset function available globally
window.asset = asset;

// Layouts
import InternalLayout from './layouts/InternalLayout';
import AuthLayout from './layouts/AuthLayout';

// Páginas
import Signin from './pages/auth/Signin';
import { InicioPage } from '@/pages/inicio/page';
import { OrdensServicoPage } from '@/pages/ordens-servico/page';
import { OrdemServicoFormPage } from '@/pages/ordens-servico/form';
import { ImprimirOSPage } from '@/pages/ordens-servico/imprimir';
import { AgendaAgendamentosPage } from '@/pages/agenda/agendamentos';
import { ArrumarTiposPage } from '@/pages/agenda/arrumar-tipos';
import { AgendaInstalacoesPage } from '@/pages/agenda/instalacoes-celular';
/* As duas `-antiga` são as telas como estavam antes da limpeza de 25/08,
   guardadas para conferência. Saem quando o redesenho fechar. */
import { AgendaInstalacoesAntigaPage } from '@/pages/agenda/instalacoes-antiga';
import { AgendaAgendamentosAntigaPage } from '@/pages/agenda/agendamentos-antiga';
import { RoteiroDiaPage } from '@/pages/agenda/roteiro-dia';
import { AjudaPage } from '@/pages/ajuda/page';
import { AjudaArtigoPage } from '@/pages/ajuda/artigo';
import { UsuariosPage } from '@/pages/usuarios/page';
import { PerfisAcessosPage } from '@/pages/perfis-acessos/page';
import { ViagensPage } from '@/pages/viagens/page';
import { ViagemFormPage } from '@/pages/viagens/form';
import { ViagemDetalhePage } from '@/pages/viagens/detalhe';
import { DespesasPage } from '@/pages/despesas/page';
import { DespesaNovaPage } from '@/pages/despesas/nova';
import { DespesaFormCompletoPage } from '@/pages/despesas/form-completo';
import { MinhasDespesasPage } from '@/pages/despesas/minhas-celular';
import { LancarDespesaPage } from '@/pages/despesas/publico';
import { InstalarPage } from '@/pages/publico/instalar/page';
import { FuncionariosPage } from '@/pages/funcionarios/page';
import { FuncionarioFormPage } from '@/pages/funcionarios/form';
import { DevCamposNumericosPage } from '@/pages/dev/campos-numericos/page';
import { DevMensagensPage } from '@/pages/dev/mensagens/page';
import { DevHeaderPage } from '@/pages/dev/header/page';
import {
  DevMolduraPaginaPage,
  DevMolduraInboxPage,
  DevMolduraOsPage,
} from '@/pages/dev/moldura-pagina/page';
import { DevTelasCelularPage } from '@/pages/dev/telas-celular/page';
import { AutorizacoesPage } from '@/pages/autorizacoes/page';
import { AutorizacaoFormPage } from '@/pages/autorizacoes/form';
import { AutorizacaoDetalhePage } from '@/pages/autorizacoes/detalhe';
import { AutorizacaoEtapaPage } from '@/pages/autorizacoes/etapa';
import { TarefaDetalhePage } from '@/pages/tarefas/detalhe';
import { ConfiguracoesPage } from '@/pages/configuracoes/page';
import { AparenciaPage } from '@/pages/minha-area/aparencia/page';
import { NotificacoesPage } from '@/pages/minha-area/notificacoes/page';
import InboxPage from '@/pages/inbox/page';
import { MeuPerfilPage } from '@/pages/meu-perfil/page';
import { MeusDadosPage } from '@/pages/meus-dados/page';
import { CustosPedidoListaPage } from '@/pages/custos-pedido/page';
import { CustosPedidoFormPage } from '@/pages/custos-pedido/form';
import { CustosPedidoDashboardPage } from '@/pages/custos-pedido/dashboard';
import { OrdensCompraListaPage } from '@/pages/custos-pedido/ordens-compra/page';
import { OrdemCompraFormPage } from '@/pages/custos-pedido/ordens-compra/form';
import { OrdensCompraFornecedoresPage } from '@/pages/custos-pedido/ordens-compra/fornecedores';
import { EmConstrucao } from '@/pages/em-construcao';

const RouteObserver = () => {
  const location = useLocation();
  useEffect(() => {
    updateCoverImages();
    updateChoices();
    window.scrollTo(0, 0);
    const t1 = setTimeout(updateCoverImages, 200);
    const t2 = setTimeout(updateChoices, 200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location]);
  return null;
};

/**
 * A rampa de `/pessoal/funcionarios/:id` — precisa de componente porque
 * `<Navigate>` não sabe reconstruir o caminho com o parâmetro: `to` é uma string
 * literal, e `"/funcionarios/:id"` levaria a pessoa para uma OS chamada ":id".
 *
 * Aqui o `useParams` lê o id que a rota casou e monta o destino de verdade.
 * `replace` para o botão "voltar" não devolver ao endereço morto.
 */
const NavegarParaFichaDoFuncionario: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={`/funcionarios/${id}`} replace />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router basename={import.meta.env.BASE_URL}>
          <RouteObserver />
          {/* Faixa de "saiu versão nova". Fora do ErrorBoundary e de qualquer
              layout de propósito: tem que aparecer em toda tela, inclusive nas
              públicas e na de login. */}
          <CustomAvisoNovaVersao />
          {/* Tarja do "ver como" — fora dos layouts, para aparecer em qualquer
              tela enquanto a sessão estiver emprestada. */}
          <CustomFaixaVerComo />
          <Toaster position="top-right" richColors />
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<Navigate to="/inicio" replace />} />

              {/* Território `public` — sem login, protegido pelo próprio link.
                  A grafia acompanha a da API (`/api/public/…`): as camadas leem
                  igual. Ver `.claude/docs/projeto/09-convencao-api.md`. */}
              <Route path="/public/lancar-despesa" element={<LancarDespesaPage />} />
              {/* Sem login de propósito: o link vai por WhatsApp e a pessoa
                  instala o app antes de ter usuário e senha em mãos. */}
              <Route path="/public/instalar" element={<InstalarPage />} />

              {/* RAMPAS — endereços velhos, 18/08/2026.
                  Estes links circulam em conversa de WhatsApp que ninguém apaga
                  e podem estar como atalho na tela inicial do celular do
                  instalador. NÃO têm data para sair: só saem com aval do
                  Vittorio, e depois de o registro do backend
                  (`CAMINHO ANTIGO` no log) ficar em silêncio. */}
              <Route
                path="/publico/lancar-despesa"
                element={<Navigate to="/public/lancar-despesa" replace />}
              />
              <Route
                path="/publico/instalar"
                element={<Navigate to="/public/instalar" replace />}
              />
              {/* RAMPAS de tela — 18/08/2026, alinhamento com a árvore do app
                  (seção do RBAC ↔ rota ↔ pasta ↔ API). Endereço de tela vive
                  fora do repositório: favorito, PWA instalado, link colado em
                  conversa. Estas saem quando ele mandar, não por prazo. */}
              <Route path="/perfil" element={<Navigate to="/pessoal/acesso" replace />} />
              <Route
                path="/perfis-acessos"
                element={<Navigate to="/configuracoes/perfis" replace />}
              />
              {/* Funcionários saiu de `/pessoal/` em 28/08: grupo de menu não
                  entra na URL neste app, e aquele prefixo era a única exceção —
                  herdada de quando "Pessoal" queria dizer departamento. */}
              <Route
                path="/pessoal/funcionarios"
                element={<Navigate to="/funcionarios" replace />}
              />
              <Route
                path="/pessoal/funcionarios/:id"
                element={<NavegarParaFichaDoFuncionario />}
              />
              {/* Duas rampas para a MESMA tela, e as duas continuam valendo:
                  `/preferencias` é o endereço mais antigo (de antes do prefixo)
                  e `/pessoal/preferencias` é o de 18/08. A tela virou
                  `/minha-area/aparencia` em 28/08 — o nome antigo prometia
                  também os avisos, que hoje são outra página. */}
              <Route
                path="/preferencias"
                element={<Navigate to="/minha-area/aparencia" replace />}
              />
              <Route
                path="/pessoal/preferencias"
                element={<Navigate to="/minha-area/aparencia" replace />}
              />

              {/* RAMPA mais antiga ainda, de antes de existir o prefixo. */}
              <Route
                path="/lancar-despesa"
                element={<Navigate to="/public/lancar-despesa" replace />}
              />

              {/* Bancada do Valor Rápido — laboratório, não é tela do app. Só
                  existe em desenvolvimento (`vite dev`): `import.meta.env.DEV` é
                  avaliado no bundle, então a página e a rota somem do build de
                  produção. Fora do RequireAuth de propósito — abre sem backend. */}
              {import.meta.env.DEV && (
                <Route path="/dev/campos-numericos" element={<DevCamposNumericosPage />} />
              )}


              {/* Bancada do header — mesma regra da de cima: só em `vite dev`,
                  fora do RequireAuth. Existe porque conferir o header exige ver
                  as combinações de tema, e vê-las dentro do app exige sessão. */}
              {import.meta.env.DEV && (
                <Route path="/dev/header" element={<DevHeaderPage />} />
              )}


              {/* A MESMA tela de Aparência, alcançável sem sessão — não é uma
                  cópia, é a mesma `AparenciaPage`. Existe pelo motivo escrito
                  logo acima: conferir uma tela de tema exige olhá-la, e olhá-la
                  dentro do app exige login. Só em `vite dev`; some do build de
                  produção junto com as outras duas bancadas. */}
              {import.meta.env.DEV && (
                <Route path="/dev/aparencia" element={<AparenciaPage />} />
              )}

              {/* A tela de Configuracoes, alcancavel sem sessao — mesma regra
                  e mesmo motivo das outras bancadas. Nao e copia: e a mesma
                  ConfiguracoesPage. Some do build de producao. */}
              {import.meta.env.DEV && (
                <Route path="/dev/configuracoes" element={<ConfiguracoesPage />} />
              )}

              {import.meta.env.DEV && (
                <Route path="/dev/notificacoes" element={<NotificacoesPage />} />
              )}

              {/* Pública */}
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<Signin />} />
              </Route>

              {/* Protegidas */}
              <Route element={<RequireAuth />}>
                {/* Impressão/PDF — view limpa, sem menu */}
                <Route
                  path="/ordens-servico/:id/imprimir"
                  element={<ImprimirOSPage />}
                />
                <Route path="/agenda/roteiro" element={<RoteiroDiaPage />} />
                <Route element={<InternalLayout />}>
                  <Route path="/inicio" element={<InicioPage />} />
                  {/* Bancada do motor de mensagens — laboratório, não é tela do app.
                      DENTRO do layout autenticado porque o motor precisa de sessão
                      (quem vê uma sala herdada é quem vê o item). Só em `vite dev`. */}
                  {import.meta.env.DEV && (
                    <Route path="/dev/mensagens" element={<DevMensagensPage />} />
                  )}
                  <Route path="/pessoal/acesso" element={<MeuPerfilPage />} />
                  {/* Pessoal. "Meus dados" mora aqui dentro: é a ficha da
                      própria pessoa, e o caminho antigo redireciona para não
                      quebrar link salvo. Quem não é funcionário (sócio,
                      contador) não tem ficha — usa "Meu perfil". */}
                  {/* `/funcionarios`, e não `/equipe/funcionarios` nem
                      `/pessoal/funcionarios`: GRUPO DE MENU NÃO ENTRA NA URL
                      neste app — `/ordens-servico` não é
                      `/operacao/ordens-servico`, `/despesas` não é
                      `/financeiro/despesas`. O prefixo `/pessoal/` sempre foi a
                      exceção, e ela vinha de quando "Pessoal" era o nome do
                      departamento.

                      Agora as quatro camadas voltam a contar a mesma história:
                      permissão `funcionarios.ficha.ver`, API `/api/funcionarios`,
                      rota `/funcionarios`, pasta `pages/funcionarios/`. */}
                  <Route path="/funcionarios" element={<FuncionariosPage />} />
                  <Route path="/funcionarios/:id" element={<FuncionarioFormPage />} />
                  <Route path="/pessoal/meus-dados" element={<MeusDadosPage />} />
                  <Route
                    path="/meus-dados"
                    element={<Navigate to="/pessoal/meus-dados" replace />}
                  />
                  {/* UMA PÁGINA POR LOJA (29/10/2026): `/ordens-servico/loja/bz`.
                      `/ordens-servico` segue valendo — é o favorito de todo
                      mundo — e a própria página leva à primeira loja da pessoa.
                      O segmento fixo `loja` vem antes de `:id` (o React Router
                      já dá prioridade ao fixo; a ordem aqui é para quem lê). */}
                  <Route path="/ordens-servico" element={<OrdensServicoPage />} />
                  <Route
                    path="/ordens-servico/loja/:loja"
                    element={<OrdensServicoPage />}
                  />
                  <Route
                    path="/ordens-servico/nova"
                    element={<OrdemServicoFormPage />}
                  />
                  <Route
                    path="/ordens-servico/:id"
                    element={<OrdemServicoFormPage />}
                  />
                  {/* DUAS agendas, telas diferentes porque o trabalho é
                      diferente: `agendamentos` é onde a Suany MARCA (OS a
                      agendar, arrastar, editar); `instalacoes` é o que o
                      instalador vê no celular — a semana, e só.
                      `/agenda` cai em Agendamentos: é quem usa o link antigo. */}
                  <Route
                    path="/agenda"
                    element={<Navigate to="/agenda/agendamentos" replace />}
                  />
                  <Route
                    path="/agenda/agendamentos"
                    element={<AgendaAgendamentosPage />}
                  />
                  {/* A retipagem (Fase 1b): o que era uma reunião com a Suany
                      virou uma tela que ela resolve sozinha quando passar. */}
                  <Route
                    path="/agenda/arrumar-tipos"
                    element={<ArrumarTiposPage />}
                  />
                  <Route
                    path="/agenda/agendamentos-antiga"
                    element={<AgendaAgendamentosAntigaPage />}
                  />
                  <Route
                    path="/agenda/instalacoes"
                    element={<AgendaInstalacoesPage />}
                  />
                  {/* As duas telas da agenda são UMA PÁGINA POR LOJA, como as OS
                      (29/10/2026). Sem loja = "Todas", só para quem tem as duas;
                      a própria página leva quem tem uma só à dela. */}
                  <Route
                    path="/agenda/instalacoes/loja/:loja"
                    element={<AgendaInstalacoesPage />}
                  />
                  <Route
                    path="/agenda/agendamentos/loja/:loja"
                    element={<AgendaAgendamentosPage />}
                  />
                  <Route
                    path="/agenda/instalacoes-antiga"
                    element={<AgendaInstalacoesAntigaPage />}
                  />
                  {/* Bancada de telas no celular. Ao contrário da bancada dos
                      campos numéricos, esta VAI para produção de propósito: ela
                      existe para ele abrir no app instalado, no aparelho, e
                      dizer o que incomoda — e isso não dá para fazer em
                      `vite dev`. Fica atrás da permissão de administrar acesso,
                      e sai quando a decisão 5 do visual fechar. */}
                  <Route path="/dev/telas-celular" element={<DevTelasCelularPage />} />

                  {/* Bancada da MOLDURA DA PÁGINA. Vai AQUI DENTRO, e não no
                      bloco público das outras bancadas, por um motivo de
                      desenho: a moldura vive dentro do `adminuiux-content`,
                      encostada no menu lateral e embaixo do cabeçalho. Julgar o
                      topo de uma página sem o casco do app em volta é julgar
                      outra coisa. Só em `vite dev` — some do build de produção.

                      As duas rotas filhas não são enfeite: elas provam o defeito
                      que originou a frente (Inbox → OS → o Voltar tem de dizer
                      "Inbox", não devolver para a lista). Sem duas telas de
                      verdade não há trilha para demonstrar. */}
                  {import.meta.env.DEV && (
                    <Route path="/dev/moldura-pagina" element={<DevMolduraPaginaPage />} />
                  )}
                  {import.meta.env.DEV && (
                    <Route path="/dev/moldura-pagina/inbox" element={<DevMolduraInboxPage />} />
                  )}
                  {import.meta.env.DEV && (
                    <Route path="/dev/moldura-pagina/os" element={<DevMolduraOsPage />} />
                  )}
                  <Route path="/ajuda" element={<AjudaPage />} />
                  <Route path="/ajuda/:slug" element={<AjudaArtigoPage />} />
                  <Route
                    path="/agenda/teste-1"
                    element={<Navigate to="/agenda/agendamentos" replace />}
                  />
                  <Route path="/estoque" element={<EmConstrucao titulo="Estoque" />} />
                  <Route path="/viagens" element={<ViagensPage />} />
                  <Route path="/viagens/nova" element={<ViagemFormPage />} />
                  <Route path="/viagens/:id/editar" element={<ViagemFormPage />} />
                  <Route path="/viagens/:id" element={<ViagemDetalhePage />} />
                  <Route
                    path="/pos-venda"
                    element={<EmConstrucao titulo="Pós-venda" />}
                  />
                  <Route
                    path="/ocorrencias"
                    element={<EmConstrucao titulo="Ocorrências" />}
                  />
                  {/* Custos de Pedido — lista por loja, dashboard e lançamento */}
                  {/* Ordens de compra (ficha #42) — ANTES de `/custos-pedido/:id`. */}
                  <Route path="/custos-pedido/ordens-compra" element={<OrdensCompraListaPage />} />
                  <Route path="/custos-pedido/ordens-compra/nova" element={<OrdemCompraFormPage />} />
                  <Route path="/custos-pedido/ordens-compra/fornecedores" element={<OrdensCompraFornecedoresPage />} />
                  <Route path="/custos-pedido/ordens-compra/:id" element={<OrdemCompraFormPage />} />
                  <Route path="/custos-pedido/lista/:loja" element={<CustosPedidoListaPage />} />
                  <Route path="/custos-pedido/novo/:loja" element={<CustosPedidoFormPage />} />
                  <Route path="/custos-pedido/:id/editar" element={<CustosPedidoFormPage />} />
                  <Route path="/custos-pedido/:id" element={<CustosPedidoDashboardPage />} />
                  <Route path="/despesas" element={<DespesasPage />} />
                  {/* A mesma lista, presa a UMA loja (25/09) — ver CustomSidebar. */}
                  <Route path="/despesas/lista/:loja" element={<DespesasPage />} />
                  {/* DUAS PORTAS, DOIS ARRANJOS (a regra é uma só, em
                      useDespesaForm). `/nova` é o lançamento em campo, pelo
                      atalho do rodapé do app instalado: coluna única, controles
                      grandes. `/lancar` e a edição são o formulário de
                      escritório: duas colunas, nada recolhido. As rotas fixas
                      vêm antes de `/:id`, senão viram um id. */}
                  <Route path="/despesas/minhas" element={<MinhasDespesasPage />} />
                  <Route path="/despesas/nova" element={<DespesaNovaPage />} />
                  <Route path="/despesas/lancar" element={<DespesaFormCompletoPage />} />
                  <Route path="/despesas/:id" element={<DespesaFormCompletoPage />} />
                  <Route path="/autorizacoes" element={<AutorizacoesPage />} />
                  <Route
                    path="/autorizacoes/nova"
                    element={<AutorizacaoFormPage />}
                  />
                  {/* ⚠️ A ETAPA vem ANTES do `/:id` só por leitura: são
                      caminhos distintos e o router não confunde, mas manter o
                      mais específico em cima é a regra que já vale na API. */}
                  <Route
                    path="/autorizacoes/:id/etapas/:etapaId"
                    element={<AutorizacaoEtapaPage />}
                  />
                  <Route
                    path="/autorizacoes/:id"
                    element={<AutorizacaoDetalhePage />}
                  />
                  {/* A tarefa por ela mesma. A ETAPA continua abrindo dentro da
                      autorização (esta página redireciona quando percebe); esta
                      rota existe para a OBRIGAÇÃO — alvará, IPTU —, que não tem
                      autorização nenhuma e, até 23/09, não tinha para onde ir
                      quando alguém clicava nela na agenda. */}
                  <Route path="/tarefas/:id" element={<TarefaDetalhePage />} />
                  <Route path="/usuarios" element={<UsuariosPage />} />
                  <Route
                    path="/configuracoes/perfis"
                    element={<PerfisAcessosPage />}
                  />
                  {/* A INBOX é tela de COMPOSIÇÃO, irmã do Início: sem módulo, sem tabela,
                      sem permissão própria. A aba vai na query string (`?aba=`),
                      no padrão que a OS já usa — é o que permite mandar o link
                      de uma aba por WhatsApp. */}
                  <Route path="/inbox" element={<InboxPage />} />
                  <Route path="/configuracoes" element={<ConfiguracoesPage />} />
                  <Route path="/minha-area/aparencia" element={<AparenciaPage />} />
                  <Route path="/minha-area/notificacoes" element={<NotificacoesPage />} />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/inicio" replace />} />
            </Routes>
          </ErrorBoundary>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
